import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const FlagPT = () => (
  <svg viewBox="0 0 640 480" className="w-5 h-3.5 rounded-[2px] shadow-sm flex-shrink-0">
    <path fill="#006600" d="M0 0h256v480H0z" />
    <path fill="#FF0000" d="M256 0h384v480H256z" />
    <circle cx="256" cy="240" r="80" fill="#FFCC00" stroke="#006600" strokeWidth="4" />
    <circle cx="256" cy="240" r="55" fill="#FF0000" />
    <path d="M256 195c-24 0-44 20-44 45s20 45 44 45 44-20 44-45-20-45-44-45z" fill="#FFF" />
  </svg>
);

const FlagFR = () => (
  <svg viewBox="0 0 640 480" className="w-5 h-3.5 rounded-[2px] shadow-sm flex-shrink-0">
    <path fill="#002395" d="M0 0h213.3v480H0z" />
    <path fill="#FFF" d="M213.3 0h213.4v480H213.3z" />
    <path fill="#ED2939" d="M426.7 0H640v480H426.7z" />
  </svg>
);

const FlagGB = () => (
  <svg viewBox="0 0 640 480" className="w-5 h-3.5 rounded-[2px] shadow-sm flex-shrink-0">
    <path fill="#012169" d="M0 0h640v480H0z" />
    <path fill="#FFF" d="M75 0l244 181L562 0h78v62L400 241l240 178v61h-80L320 301 82 480H0v-60l239-178L0 64V0h75z" />
    <path fill="#C8102E" d="M424 281l216 159v40L369 281h55zm-184 20l6 35L54 480H0l240-179zM640 0v3L391 191l2-44L590 0h50zM0 0l239 176h-60L0 42V0z" />
    <path fill="#FFF" d="M241 0v480h160V0H241zM0 160v160h640V160H0z" />
    <path fill="#C8102E" d="M0 193v96h640v-96H0zM273 0v480h96V0h-96z" />
  </svg>
);

const LANGUAGES = [
  { code: 'pt', label: 'PT', Flag: FlagPT, name: 'Português' },
  { code: 'fr', label: 'FR', Flag: FlagFR, name: 'Français' },
  { code: 'en', label: 'EN', Flag: FlagGB, name: 'English' },
];

export default function LanguageSwitcher({ variant = 'default' }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[2];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const switchLang = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  const isCompact = variant === 'compact';
  const CurrentFlag = current.Flag;

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <button
        onClick={() => setOpen(prev => !prev)}
        className={`flex items-center gap-2 rounded-full border transition-all ${
          isCompact
            ? 'px-2.5 py-1.5 text-xs border-[#E5E3DB]/80 hover:border-[#6E1A24]/30 bg-white/80'
            : 'px-3.5 py-2 text-sm border-[#E5E3DB] hover:border-[#6E1A24]/30 bg-white/70 backdrop-blur-sm hover:bg-white/90'
        }`}
        data-testid="language-switcher-trigger"
        aria-label="Change language"
      >
        <CurrentFlag />
        <span className="font-medium text-[#2D2D2A] tracking-wide">{current.label}</span>
        <svg
          className={`text-[#9C9C97] transition-transform duration-200 ${open ? 'rotate-180' : ''} ${isCompact ? 'w-3 h-3' : 'w-3.5 h-3.5'}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 bg-white rounded-xl border border-[#E5E3DB]/80 shadow-xl shadow-black/[0.08] overflow-hidden z-50 min-w-[160px]">
          {LANGUAGES.map(lang => {
            const LangFlag = lang.Flag;
            const isActive = lang.code === current.code;
            return (
              <button
                key={lang.code}
                onClick={() => switchLang(lang.code)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                  isActive
                    ? 'bg-[#6E1A24]/[0.04] text-[#6E1A24]'
                    : 'text-[#2D2D2A] hover:bg-[#F5F2EB]'
                }`}
                data-testid={`lang-option-${lang.code}`}
              >
                <LangFlag />
                <span className={isActive ? 'font-semibold' : 'font-medium'}>{lang.name}</span>
                {isActive && (
                  <svg className="w-4 h-4 ml-auto text-[#6E1A24]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
